import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('wvc_token'));
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);

  // Validate session on app load
  const validateSession = useCallback(async () => {
    const storedToken = localStorage.getItem('wvc_token');
    
    if (!storedToken) {
      setUser(null);
      setToken(null);
      setIsLoading(false);
      return false;
    }

    try {
      console.log('[Auth] Validating session...');
      const response = await axios.get(`${API_URL}/auth/me?token=${storedToken}`, {
        timeout: 10000
      });
      
      if (response.data && response.data.user_id) {
        setUser(response.data);
        setToken(storedToken);
        console.log('[Auth] Session valid:', response.data.name);
        return true;
      } else {
        throw new Error('Invalid user data');
      }
    } catch (error) {
      console.error('[Auth] Session validation failed:', error.message);
      // Clear invalid session
      localStorage.removeItem('wvc_token');
      setUser(null);
      setToken(null);
      
      // Only show message if token existed (expired session)
      if (storedToken && error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initialize auth state on mount
  useEffect(() => {
    validateSession();
  }, [validateSession]);

  // Handle successful login
  const handleLoginSuccess = useCallback((userData, sessionToken) => {
    console.log('[Auth] Login success:', userData.name);
    setUser(userData);
    setToken(sessionToken);
    localStorage.setItem('wvc_token', sessionToken);
    setShowLoginModal(false);
    toast.success(`Welcome, ${userData.name}!`);

    // Execute pending action if exists
    if (pendingAction) {
      console.log('[Auth] Executing pending action:', pendingAction.type);
      setTimeout(() => {
        pendingAction.callback();
        setPendingAction(null);
      }, 100);
    }
  }, [pendingAction]);

  // Google Login - uses redirect for mobile compatibility
  const loginWithGoogle = useCallback(() => {
    setIsAuthenticating(true);
    console.log('[Auth] Starting Google login...');
    
    // Store current path for redirect after auth
    const currentPath = window.location.pathname;
    localStorage.setItem('wvc_auth_redirect', currentPath);
    
    // Use redirect-based auth (mobile-friendly) - use origin only for consistent redirect
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  // Process Google OAuth callback
  const processGoogleCallback = useCallback(async (sessionId) => {
    setIsAuthenticating(true);
    console.log('[Auth] Processing Google callback...');
    
    try {
      const response = await axios.post(`${API_URL}/auth/google/session`, {
        session_id: sessionId
      }, { timeout: 15000 });

      if (response.data?.user && response.data?.session_token) {
        handleLoginSuccess(response.data.user, response.data.session_token);
        return true;
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('[Auth] Google login failed:', error.response?.data || error.message);
      const message = error.response?.data?.detail || 'Google login failed. Please try again.';
      toast.error(message);
      return false;
    } finally {
      setIsAuthenticating(false);
    }
  }, [handleLoginSuccess]);

  // Email OTP - Request OTP
  const requestEmailOTP = useCallback(async (email) => {
    setIsAuthenticating(true);
    console.log('[Auth] Requesting OTP for:', email);
    
    try {
      const response = await axios.post(`${API_URL}/auth/request-otp`, { email }, { timeout: 10000 });
      toast.success('OTP sent to your email!');
      return { success: true, data: response.data };
    } catch (error) {
      console.error('[Auth] OTP request failed:', error.response?.data || error.message);
      const message = error.response?.data?.detail || 'Failed to send OTP. Please try again.';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setIsAuthenticating(false);
    }
  }, []);

  // Email OTP - Verify OTP
  const verifyEmailOTP = useCallback(async (email, otp, name = null) => {
    setIsAuthenticating(true);
    console.log('[Auth] Verifying OTP...');
    
    try {
      const response = await axios.post(`${API_URL}/auth/verify-otp`, { 
        email, 
        otp,
        name 
      }, { timeout: 10000 });

      if (response.data?.user && response.data?.session_token) {
        handleLoginSuccess(response.data.user, response.data.session_token);
        return { success: true };
      } else {
        throw new Error('Invalid response');
      }
    } catch (error) {
      console.error('[Auth] OTP verification failed:', error.response?.data || error.message);
      const message = error.response?.data?.detail || 'Invalid OTP. Please try again.';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setIsAuthenticating(false);
    }
  }, [handleLoginSuccess]);

  // Email + Password Login (fallback)
  const loginWithEmail = useCallback(async (email, password) => {
    setIsAuthenticating(true);
    console.log('[Auth] Email login attempt...');
    
    try {
      const response = await axios.post(`${API_URL}/auth/login`, { email, password }, { timeout: 10000 });

      if (response.data?.user && response.data?.session_token) {
        handleLoginSuccess(response.data.user, response.data.session_token);
        return { success: true };
      } else {
        throw new Error('Invalid response');
      }
    } catch (error) {
      console.error('[Auth] Email login failed:', error.response?.data || error.message);
      const message = error.response?.data?.detail || 'Invalid email or password.';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setIsAuthenticating(false);
    }
  }, [handleLoginSuccess]);

  // Register new user
  const registerUser = useCallback(async (email, password, name) => {
    setIsAuthenticating(true);
    console.log('[Auth] Registering new user...');
    
    try {
      const response = await axios.post(`${API_URL}/auth/register`, { 
        email, 
        password, 
        name 
      }, { timeout: 10000 });

      if (response.data?.user && response.data?.session_token) {
        handleLoginSuccess(response.data.user, response.data.session_token);
        return { success: true };
      } else {
        throw new Error('Invalid response');
      }
    } catch (error) {
      console.error('[Auth] Registration failed:', error.response?.data || error.message);
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setIsAuthenticating(false);
    }
  }, [handleLoginSuccess]);

  // Logout
  const logout = useCallback(async () => {
    console.log('[Auth] Logging out...');
    
    try {
      if (token) {
        await axios.post(`${API_URL}/auth/logout?token=${token}`, {}, { timeout: 5000 });
      }
    } catch (error) {
      console.error('[Auth] Logout API error:', error.message);
    }
    
    localStorage.removeItem('wvc_token');
    localStorage.removeItem('wvc_auth_redirect');
    setUser(null);
    setToken(null);
    toast.success('Logged out successfully');
  }, [token]);

  // Require login - shows modal and sets pending action
  const requireLogin = useCallback((actionType, callback) => {
    if (user) {
      callback();
      return true;
    }
    
    console.log('[Auth] Login required for:', actionType);
    setPendingAction({ type: actionType, callback });
    setShowLoginModal(true);
    return false;
  }, [user]);

  // Check if user is logged in
  const isLoggedIn = Boolean(user && token);

  const value = {
    user,
    token,
    isLoading,
    isAuthenticating,
    isLoggedIn,
    showLoginModal,
    setShowLoginModal,
    pendingAction,
    loginWithGoogle,
    processGoogleCallback,
    requestEmailOTP,
    verifyEmailOTP,
    loginWithEmail,
    registerUser,
    logout,
    requireLogin,
    validateSession
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
